// Promos que el vendedor marca en «Elige el teléfono» antes de apartar, y cómo pasan a la selección
// de la sesión y al desglose del precio. Puro, sin React.
import type {
    PhonePriceRow,
    PhonePricing,
    SalePromotionTone,
} from '@/domains/sales/wizard/promotionSlots';
import type { CatalogItem, ReservedLine } from '@/domains/sales/wizard/types';
import type {
    EvaluatedSalePromotion,
    EvaluateSalePromotionsResponse,
    SalePromotionLever,
    StoreSalePromotionItem,
} from '../api/wizardPromotionsContract';
import { lineDiscounts } from './applySelection';
import { isSureKeepOnly } from './promotionAppliesTo';
import { blockedReasonCopy } from './promotionErrorCopy';
import {
    choicesOn,
    EMPTY_LINE,
    type PromotionChoice,
    type PromotionSelection,
} from './promotionSelection';

/** Una promo del teléfono como la ve el selector. */
export interface DraftOption {
    promotionId: string;
    version: number;
    name: string;
    stackable: boolean;
    tone: SalePromotionTone;
    /** Lo que baja el precio del teléfono; 0 si no lo baja. */
    discountCents: number;
    sureKeepOnly: boolean;
    /** Alcanza a toda la tienda: va como descuento de tienda en el desglose. */
    storeWide: boolean;
    /** El regalo de una sola opción, que se aparta junto con la promo. */
    giftSkus: string[];
    /** Regalo de varias opciones: se elige en «Arma la venta». */
    giftPending: boolean;
    /** Por qué no se puede elegir; `null` si se puede. */
    unavailable: string | null;
    /** Se puede marcar sola por defecto. */
    preselectable: boolean;
}

/** Lo que el vendedor marcó para un teléfono. */
export interface PhoneDraft {
    sku: string;
    options: DraftOption[];
    soloId: string | null;
    stackableIds: string[];
    /** «Sin promo»: no quiere ninguna de las que van solas. */
    declined: boolean;
    code: string | null;
}

/** Palancas que nunca se marcan solas: dan algo después de la venta o son de un solo uso. */
const NEVER_PRESELECTED: readonly SalePromotionLever[] = [
    'COUPON',
    'EXTENDED_WARRANTY',
    'WALLET_BONUS',
    'AIRTIME',
];

/** Tono de la palanca principal: descuento, regalo, beneficio y cupón, en ese orden. */
export function toneOfLevers(levers: readonly SalePromotionLever[]): SalePromotionTone {
    if (levers.includes('DEVICE_DISCOUNT') || levers.includes('ACCESSORY_DISCOUNT')) {
        return 'discount';
    }
    if (levers.includes('GIFT')) return 'gift';
    if (levers.some((l) => l === 'WALLET_BONUS' || l === 'AIRTIME' || l === 'EXTENDED_WARRANTY')) {
        return 'benefit';
    }
    return levers.includes('COUPON') ? 'coupon' : 'benefit';
}

/** Alcance de toda la tienda: ni SKU, ni gama, ni categoría. */
export function isWholeStore(promo: Pick<StoreSalePromotionItem, 'scope'>): boolean {
    const { skus, tiers, productCategories } = promo.scope;
    return skus === null && tiers === null && productCategories === null;
}

/** Lo que baja el teléfono: su renglón si se conoce la línea; si no, el total de la promo. */
function phoneDiscountOf(promo: EvaluatedSalePromotion, anchorLineId: string | undefined): number {
    if (!promo.levers.includes('DEVICE_DISCOUNT')) return 0;
    const line = anchorLineId ? promo.lines.find((l) => l.lineId === anchorLineId) : undefined;
    return line ? line.discountCents : promo.totalDiscountCents;
}

function optionOf(
    promo: EvaluatedSalePromotion,
    storeWideIds: ReadonlySet<string>,
    anchorLineId: string | undefined,
): DraftOption {
    const inStock = promo.giftOptions.filter((o) => o.inStock);
    const outOfStock = promo.giftOptions.length > 0 && inStock.length === 0;
    const giftPending = inStock.length > 1;
    const unavailable = promo.blockedBy
        ? blockedReasonCopy(promo.blockedBy)
        : outOfStock
          ? 'El regalo no tiene existencia'
          : null;
    return {
        promotionId: promo.promotionId,
        version: promo.version,
        name: promo.name,
        stackable: promo.stackable,
        tone: toneOfLevers(promo.levers),
        discountCents: phoneDiscountOf(promo, anchorLineId),
        sureKeepOnly: isSureKeepOnly(promo),
        storeWide: storeWideIds.has(promo.promotionId),
        giftSkus: giftPending ? [] : inStock.map((o) => o.sku),
        giftPending,
        unavailable,
        preselectable:
            !promo.stackable &&
            unavailable === null &&
            !giftPending &&
            promo.afterSale.length === 0 &&
            !promo.levers.some((l) => NEVER_PRESELECTED.includes(l)),
    };
}

/** Su único efecto es bajar accesorios: va en «Sobre accesorios», no en el selector. */
export function onlyLowersAccessories(promo: Pick<EvaluatedSalePromotion, 'levers'>): boolean {
    return promo.levers.length > 0 && promo.levers.every((l) => l === 'ACCESSORY_DISCOUNT');
}

/** Opciones del selector: las que van solas primero y luego las que se suman, cada bloque A-Z. */
export function draftOptionsOf(
    promos: readonly EvaluatedSalePromotion[],
    storePromotions: readonly StoreSalePromotionItem[],
    anchorLineId?: string,
): DraftOption[] {
    const storeWideIds = new Set(storePromotions.filter(isWholeStore).map((p) => p.promotionId));
    return promos
        .filter((p) => !onlyLowersAccessories(p))
        .map((p) => optionOf(p, storeWideIds, anchorLineId))
        .sort(
            (a, b) =>
                Number(a.stackable) - Number(b.stackable) || a.name.localeCompare(b.name, 'es-MX'),
        );
}

function selectable(options: readonly DraftOption[], id: string): DraftOption | undefined {
    return options.find((o) => o.promotionId === id && o.unavailable === null);
}

/** La que va sola con mayor descuento al equipo; empate, la primera A-Z (así vienen las opciones). */
function bestPreselectable(options: readonly DraftOption[]): string | null {
    let best: DraftOption | undefined;
    for (const option of options) {
        if (option.preselectable && (!best || option.discountCents > best.discountCents)) {
            best = option;
        }
    }
    return best?.promotionId ?? null;
}

/** Primera vez con este teléfono: solo la que va sola con mayor descuento, si se puede marcar. */
export function defaultDraft(
    sku: string,
    options: DraftOption[],
    code: string | null = null,
): PhoneDraft {
    return {
        sku,
        options,
        soloId: bestPreselectable(options),
        stackableIds: [],
        declined: false,
        code,
    };
}

/** Otro teléfono: se queda lo que también le aplica (el código siempre) y se quita lo demás. */
export function carryDraft(previous: PhoneDraft, sku: string, options: DraftOption[]): PhoneDraft {
    const solo = previous.soloId ? selectable(options, previous.soloId) : undefined;
    const keepSolo = solo !== undefined && !solo.stackable;
    return {
        sku,
        options,
        soloId: keepSolo ? solo.promotionId : previous.declined ? null : bestPreselectable(options),
        stackableIds: previous.stackableIds.filter((id) => selectable(options, id)?.stackable),
        declined: previous.declined,
        code: previous.code,
    };
}

/** De regreso al teléfono apartado: lo que tiene la sesión; sin nada apartado, lo de siempre. */
export function restoreDraft(
    sku: string,
    options: DraftOption[],
    held: { promotionIds: readonly string[]; code: string | null; declined: boolean },
): PhoneDraft {
    const known = held.promotionIds
        .map((id) => options.find((o) => o.promotionId === id))
        .filter((o): o is DraftOption => o !== undefined);
    if (known.length === 0 && !held.declined && held.code === null) {
        return defaultDraft(sku, options);
    }
    return {
        sku,
        options,
        soloId: known.find((o) => !o.stackable)?.promotionId ?? null,
        stackableIds: known.filter((o) => o.stackable).map((o) => o.promotionId),
        declined: held.declined,
        code: held.code,
    };
}

/** Marca la que va sola; `null` es «Sin promo». */
export function withDraftSolo(draft: PhoneDraft, promotionId: string | null): PhoneDraft {
    return { ...draft, soloId: promotionId, declined: promotionId === null };
}

export function withDraftStackable(
    draft: PhoneDraft,
    promotionId: string,
    on: boolean,
): PhoneDraft {
    const rest = draft.stackableIds.filter((id) => id !== promotionId);
    return { ...draft, stackableIds: on ? [...rest, promotionId] : rest };
}

/** Lo marcado que se puede apartar; un regalo de varias opciones solo si ya se eligió en la venta. */
export function chosenOptions(draft: PhoneDraft): DraftOption[] {
    const ids = [...(draft.soloId ? [draft.soloId] : []), ...draft.stackableIds];
    return ids
        .map((id) => selectable(draft.options, id))
        .filter((o): o is DraftOption => o !== undefined);
}

/** Promo marcada que la evaluación ya no deja apartar, con su motivo. */
export interface DraftDrop {
    promotionId: string;
    name: string;
    reason: string;
}

/** Ajusta lo marcado a la evaluación de la venta ya apartada (versión vigente y existencia). */
export function reconcileDraft(
    draft: PhoneDraft,
    evaluation: EvaluateSalePromotionsResponse,
): { draft: PhoneDraft; dropped: DraftDrop[] } {
    const dropped: DraftDrop[] = [];
    const options = draft.options.map((option) => {
        const promo = evaluation.promotions.find((p) => p.promotionId === option.promotionId);
        if (!promo) return { ...option, unavailable: 'Ya no aplica a esta venta' };
        const gifts = option.giftSkus.filter((sku) =>
            promo.giftOptions.some((o) => o.sku === sku && o.inStock),
        );
        const unavailable = promo.blockedBy
            ? blockedReasonCopy(promo.blockedBy)
            : option.giftSkus.length > 0 && gifts.length === 0
              ? 'El regalo se quedó sin existencia'
              : null;
        return { ...option, version: promo.version, giftSkus: gifts, unavailable };
    });
    const next = { ...draft, options };
    for (const id of [...(draft.soloId ? [draft.soloId] : []), ...draft.stackableIds]) {
        const option = options.find((o) => o.promotionId === id);
        if (option?.unavailable) {
            dropped.push({ promotionId: id, name: option.name, reason: option.unavailable });
        }
    }
    return { draft: next, dropped };
}

function choiceOf(option: DraftOption, held: readonly PromotionChoice[]): PromotionChoice {
    const before = held.find((c) => c.promotionId === option.promotionId);
    return {
        promotionId: option.promotionId,
        promotionVersion: option.version,
        // El regalo que ya eligió el cliente se respeta.
        giftSkus: before && before.giftSkus.length > 0 ? before.giftSkus : option.giftSkus,
    };
}

/**
 * Cambia solo la entrada del teléfono: lo marcado reemplaza las promos del selector y lo que no
 * es del selector (p. ej. de accesorios) se queda.
 */
export function withAnchorDraft(
    base: PromotionSelection,
    anchorLineId: string,
    draft: PhoneDraft,
    code: string | null,
): PromotionSelection {
    const held = choicesOn(base, anchorLineId);
    const own = new Set(draft.options.map((o) => o.promotionId));
    const line = base.byLine[anchorLineId] ?? EMPTY_LINE;
    // Sin regalo elegido, la de varias opciones no se aparta: queda para «Arma la venta».
    const chosen = chosenOptions(draft).filter(
        (o) => !o.giftPending || held.some((c) => c.promotionId === o.promotionId),
    );
    const solo = chosen.find((o) => !o.stackable);
    const foreignSolo = line.solo && !own.has(line.solo.promotionId) ? line.solo : null;
    const stackables = [
        ...chosen.filter((o) => o.stackable).map((o) => choiceOf(o, held)),
        ...line.stackables.filter((c) => !own.has(c.promotionId)),
    ].sort((a, b) => a.promotionId.localeCompare(b.promotionId));
    return {
        ...base,
        anchorLineId,
        code,
        byLine: {
            ...base.byLine,
            [anchorLineId]: { solo: solo ? choiceOf(solo, held) : foreignSolo, stackables },
        },
    };
}

/** La línea del teléfono sin lo que la evaluación no ofrece o el servidor rechazó para este teléfono. */
export function withoutUnoffered(
    selection: PromotionSelection,
    anchorLineId: string,
    evaluation: EvaluateSalePromotionsResponse,
    refused: readonly string[] = [],
): { selection: PromotionSelection; droppedIds: string[] } {
    const offered = new Set(
        evaluation.promotions
            .filter((p) => !p.blockedBy && !refused.includes(p.promotionId))
            .map((p) => p.promotionId),
    );
    const line = selection.byLine[anchorLineId];
    if (!line) return { selection, droppedIds: [] };
    const droppedIds = choicesOn(selection, anchorLineId)
        .map((c) => c.promotionId)
        .filter((id) => !offered.has(id));
    if (droppedIds.length === 0) return { selection, droppedIds };
    const keep = (c: PromotionChoice): boolean => offered.has(c.promotionId);
    return {
        selection: {
            ...selection,
            byLine: {
                ...selection.byLine,
                [anchorLineId]: {
                    solo: line.solo && keep(line.solo) ? line.solo : null,
                    stackables: line.stackables.filter(keep),
                },
            },
        },
        droppedIds,
    };
}

/** Ids de lo que el selector manda a apartar y el código; el regalo por elegir se aparta en el 05. */
export function draftSignature(draft: PhoneDraft): string {
    const ids = chosenOptions(draft)
        .filter((o) => !o.giftPending)
        .map((o) => o.promotionId)
        .sort();
    return JSON.stringify([ids, draft.code]);
}

/** Lo mismo, de lo que ya tiene la sesión en la línea del teléfono (solo promos del selector). */
export function heldSignature(
    selection: PromotionSelection,
    anchorLineId: string,
    draft: Pick<PhoneDraft, 'options'>,
): string {
    const own = new Set(draft.options.filter((o) => !o.giftPending).map((o) => o.promotionId));
    const ids = choicesOn(selection, anchorLineId)
        .map((c) => c.promotionId)
        .filter((id) => own.has(id))
        .sort();
    return JSON.stringify([ids, selection.code]);
}

/** La firma no trae promos del selector ni código. */
export function isEmptyHeld(signature: string): boolean {
    return signature === JSON.stringify([[], null]);
}

/** Nombre de una promo apartada que el selector no lista. */
export type PromotionNameOf = (promotionId: string) => string | undefined;

/** Desglose con lo que apartó el servidor en la línea del teléfono. */
function heldPricing(
    line: ReservedLine,
    item: CatalogItem,
    options: readonly DraftOption[],
    nameOf: PromotionNameOf,
    sureKeepOnlyOf: (promotionId: string) => boolean,
): PhonePricing {
    const baseCents = line.basePriceCents ?? item.cashPriceCents;
    const rows: PhonePriceRow[] = lineDiscounts(line)
        .filter((d) => d.discountCents > 0)
        .map((d) => {
            const option = options.find((o) => o.promotionId === d.promotionId);
            return {
                key: d.promotionId,
                label: option?.name ?? nameOf(d.promotionId) ?? 'Promo',
                kind: option?.storeWide ? 'store' : 'promotion',
                cents: d.discountCents,
                sureKeepOnly: option?.sureKeepOnly ?? sureKeepOnlyOf(d.promotionId),
            };
        });
    if (line.discountCode && (line.codeDiscountCents ?? 0) > 0) {
        rows.push({
            key: `code:${line.discountCode}`,
            label: `Código ${line.discountCode}`,
            kind: 'code',
            cents: line.codeDiscountCents ?? 0,
            sureKeepOnly: false,
        });
    }
    const discount = rows.reduce((sum, r) => sum + (r.cents ?? 0), 0);
    return { baseCents, rows, totalCents: Math.max(0, baseCents - discount), estimated: false };
}

/** Desglose estimado con lo marcado; el código se sabe hasta apartar. */
function draftPricing(item: CatalogItem, draft: PhoneDraft | undefined): PhonePricing {
    const baseCents = item.cashPriceCents;
    const rows: PhonePriceRow[] =
        draft?.sku === item.sku
            ? chosenOptions(draft)
                  .filter((o) => o.discountCents > 0)
                  .map((o) => ({
                      key: o.promotionId,
                      label: o.name,
                      kind: o.storeWide ? 'store' : 'promotion',
                      cents: o.discountCents,
                      sureKeepOnly: o.sureKeepOnly,
                  }))
            : [];
    if (draft?.sku === item.sku && draft.code) {
        rows.push({
            key: `code:${draft.code}`,
            label: `Código ${draft.code}`,
            kind: 'code',
            cents: null,
            sureKeepOnly: false,
            note: 'Se valida al continuar',
        });
    }
    const discount = rows.reduce((sum, r) => sum + (r.cents ?? 0), 0);
    return { baseCents, rows, totalCents: Math.max(0, baseCents - discount), estimated: true };
}

/**
 * Precio del teléfono elegido: del servidor si ya está apartado con lo marcado; si no, estimado.
 * `held` es la línea del teléfono en la sesión con su firma de lo apartado.
 */
export function phonePricing(
    item: CatalogItem,
    draft: PhoneDraft | undefined,
    held: { line: ReservedLine; signature: string } | undefined,
    nameOf: PromotionNameOf,
    sureKeepOnlyOf: (promotionId: string) => boolean,
): PhonePricing {
    const heldHere = held !== undefined && held.line.sku === item.sku;
    const matches = !draft || draft.sku !== item.sku || draftSignature(draft) === held?.signature;
    if (heldHere && matches) {
        return heldPricing(held.line, item, draft?.options ?? [], nameOf, sureKeepOnlyOf);
    }
    return draftPricing(item, draft);
}

/** El alcance llega a la línea del teléfono; lo que no se sabe del equipo (gama) no lo descarta. */
export function scopeReachesLine(
    promo: Pick<StoreSalePromotionItem, 'scope'>,
    line: Pick<ReservedLine, 'sku' | 'product' | 'components'>,
): boolean {
    const { skus, tiers, productCategories } = promo.scope;
    const category = line.product?.category ?? (line.components.length > 0 ? 'BUNDLE' : 'PHONE');
    const tier = line.product?.tier;
    return (
        (skus === null || skus.includes(line.sku)) &&
        (productCategories === null || productCategories.includes(category)) &&
        (tiers === null || !tier || tiers.includes(tier as (typeof tiers)[number]))
    );
}
